import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { TrashIcon } from '@heroicons/react/24/outline';
import { apiCall } from '../services/api';

interface BulkDeleteModalProps {
  bucketName: string;
  selectedFiles: Set<string>;
  /**
   * Reports the outcome so the listing refreshes. `resumableMessage` is passed ONLY for an
   * unfinished-but-resumable folder delete, whose `failedKeys` can legitimately be empty —
   * it tells the handler to suppress its success toast, which would otherwise contradict
   * the resumable notice already sent through `onError`.
   */
  onSuccess: (deletedCount: number, failedKeys: string[], resumableMessage?: string) => void;
  onError: (message: string) => void;
  onCancel: () => void;
}

/** A folder delete that finished, leaving only archive-blocked survivors behind. */
const ARCHIVE_PARTIAL_DELETE_CODE = 'ARCHIVE_PARTIAL_DELETE';

/** A folder delete that ran out of its server-side time budget and can be re-run. */
const FOLDER_DELETE_RESUMABLE_CODE = 'FOLDER_DELETE_RESUMABLE';

/** Fallback wording when a resumable response carries no `message`. */
const RESUMABLE_FALLBACK_MESSAGE =
  'Folder delete is unfinished. Run the delete again to continue.';

interface BulkDeleteFailure {
  key: string;
  reason: string;
}

interface BlockedFile {
  key: string;
  storageClass: string;
}

/**
 * Response body of `POST /s3/bulk-delete/:bucket`.
 *
 * A plain-key request returns exactly `{ deleted, failed }`. Every other field is
 * present ONLY when the request contained at least one folder prefix (a key ending
 * in '/'), which is why they are all optional here — see `deleteFolderPrefixes` in
 * `backend/functions/s3-proxy/handlers/bulk.js`.
 *
 * Partial outcomes arrive as `200`, not `409`, precisely so these fields survive:
 * `apiCall` in `services/api.ts` collapses any non-2xx body to `new Error(body.error)`
 * and discards everything else. The `500 FOLDER_DELETE_INCOMPLETE` and
 * `400 INVALID_KEY` outcomes are collapsed that way on purpose and surface through
 * the catch path below as their sanitized `error` message.
 */
interface BulkDeleteResponse {
  deleted?: string[];
  failed?: BulkDeleteFailure[];
  blockedFiles?: BlockedFile[];
  partial?: boolean;
  incomplete?: boolean;
  remainingEstimate?: number;
  code?: string;
  message?: string;
}

const BulkDeleteModal: React.FC<BulkDeleteModalProps> = ({
  bucketName,
  selectedFiles,
  onSuccess,
  onError,
  onCancel
}) => {
  const [confirmText, setConfirmText] = useState('');
  const [loading, setLoading] = useState(false);

  const canConfirm = confirmText === 'delete';
  const count = selectedFiles.size;

  const handleConfirm = async () => {
    if (!canConfirm) return;
    setLoading(true);
    try {
      const response: BulkDeleteResponse = await apiCall(`/s3/bulk-delete/${bucketName}`, {
        method: 'POST',
        body: JSON.stringify({ keys: Array.from(selectedFiles) })
      });

      const deleted: string[] = response.deleted ?? [];
      const failed: BulkDeleteFailure[] = response.failed ?? [];
      const blockedFiles: BlockedFile[] = response.blockedFiles ?? [];

      const isPartial = response.partial === true;
      const hasBlockedFiles = blockedFiles.length > 0;
      const isResumable = response.code === FOLDER_DELETE_RESUMABLE_CODE;

      // Objects that survived the request. Archive-blocked keys count as failures for
      // reporting purposes (`ARCHIVE_PARTIAL_DELETE`) so `handleBulkDeleteSuccess`
      // renders its mixed-count toast plus an error toast naming them, instead of a
      // plain success toast for a folder that is still visible in the listing.
      const failedKeys: string[] = Array.from(
        new Set<string>([
          ...failed.map((f) => f.key),
          ...(isPartial && hasBlockedFiles ? blockedFiles.map((f) => f.key) : [])
        ])
      );

      const resumableMessage = isResumable
        ? response.message ?? RESUMABLE_FALLBACK_MESSAGE
        : undefined;

      if (resumableMessage !== undefined) {
        // Unfinished, not failed: the server deleted what it could inside its time
        // budget and the delete can simply be run again. Surfaced through the existing
        // onError plumbing (a toast.error in FileBrowser) so the user sees the
        // unfinished-and-resumable wording, while onSuccess below still refreshes the
        // listing so the objects that WERE removed disappear from view.
        onError(resumableMessage);
      } else if (isPartial && !hasBlockedFiles && response.code === ARCHIVE_PARTIAL_DELETE_CODE) {
        // Defensive: an archive-partial response should always name its blocked keys.
        // If it ever does not, say so rather than falling through to a success toast.
        onError('Some objects could not be deleted. Please refresh and try again.');
      }

      if (resumableMessage !== undefined) {
        onSuccess(deleted.length, failedKeys, resumableMessage);
      } else {
        onSuccess(deleted.length, failedKeys);
      }
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : '';
      onError(message || 'Bulk delete failed');
      // Keep modal open — do not call onCancel
    } finally {
      setLoading(false);
    }
  };

  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.2 }}
          className="bg-white rounded-xl shadow-xl p-6 max-w-md w-full mx-4"
        >
          <div className="text-center mb-5">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-3">
              <TrashIcon className="h-6 w-6 text-red-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-900">Delete {count} {count === 1 ? 'Item' : 'Items'}</h3>
            <p className="text-sm text-gray-500 mt-1">This action cannot be undone.</p>
          </div>

          <div className="mb-5">
            <label htmlFor="bulk-delete-confirm" className="block text-sm font-medium text-gray-700 mb-1">
              Type <span className="font-semibold">delete</span> to confirm:
            </label>
            <input
              id="bulk-delete-confirm"
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500"
              placeholder="delete"
              autoFocus
            />
          </div>

          <div className="flex space-x-3">
            <button
              onClick={onCancel}
              disabled={loading}
              className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              disabled={!canConfirm || loading}
              className="flex-1 bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Deleting...
                </span>
              ) : (
                `Delete ${count} ${count === 1 ? 'Item' : 'Items'}`
              )}
            </button>
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  , document.body);
};

export default BulkDeleteModal;
